import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, CheckCircle, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PlanData {
  id: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  maxHires: number;
  period: 'monthly' | 'annual';
  features: string[];
  contract: string;
}

const PLANS_DATA: Record<string, PlanData> = {
  'essencial-mensal': {
    id: 'essencial-mensal',
    name: 'Plano Start',
    monthlyPrice: 219.90,
    annualPrice: 175.90,
    maxHires: 5,
    period: 'monthly',
    features: [
      'Até 5 contratações por mês',
      'R$ 43,98 por contratação',
      'Perfeito para quem está começando a contratar com inteligência.'
    ],
    contract: `
📑 Contrato – Plano Start Mensal (05 contratações por R$ 219,90)

CONTRATO DE PRESTAÇÃO DE SERVIÇOS – PLANO START MENSAL

Cláusula 1 – Objeto: O acesso à plataforma com direito a até 05 contratações por mês.
➡️ Explicação: Define o limite mensal de uso.

Cláusula 2 – Valor e Pagamento: R$ 219,90 por mês, cobrança mensal via Stripe.
➡️ Explicação: Especifica custo e método de pagamento.

Cláusula 3 – Obrigações do CONTRATANTE: Fornecer informações corretas e utilizar a plataforma de forma adequada.
➡️ Explicação: Determina deveres do cliente.

Cláusula 4 – Obrigações do CONTRATADO: Disponibilizar a plataforma e suporte básico.
➡️ Explicação: Define responsabilidades do Lyra RH.

Cláusula 5 – Vigência: Renovação mensal automática.
➡️ Explicação: Estabelece o ciclo contratual.

Cláusula 6 – Cancelamento: Aviso prévio de 15 dias.
➡️ Explicação: Define condição para rescisão.

Cláusula 7 – Foro: Foro da sede do CONTRATADO.
➡️ Explicação: Local para resolução de litígios.

Este contrato é regido pelas leis brasileiras e ambas as partes concordam com os termos estabelecidos.
    `
  },
  'profissional-mensal': {
    id: 'profissional-mensal',
    name: 'Plano Growth',
    monthlyPrice: 379.90,
    annualPrice: 304.90,
    maxHires: 10,
    period: 'monthly',
    features: [
      'Até 10 contratações por mês',
      'R$ 37,99 por contratação',
      'Mais contratações, menos esforço — o equilíbrio ideal para equipes em expansão.'
    ],
    contract: `
📑 Contrato – Plano Growth Mensal (10 contratações por R$ 379,90)

Cláusula 1 – Objeto: Acesso à plataforma para até 10 contratações mensais.
➡️ Explicação: Determina o volume incluído.

Cláusula 2 – Valor e Pagamento: R$ 379,90 por mês, cobrança mensal via Stripe.
➡️ Explicação: Define custo e cobrança.

Cláusula 3 – Benefícios: Melhor custo-benefício e aumento de produtividade.
➡️ Explicação: Destaca as vantagens do plano.

Cláusula 4 – Obrigações do CONTRATANTE: Responsabilidade pelos dados fornecidos.
➡️ Explicação: Protege a integridade do sistema.

Cláusula 5 – Obrigações do CONTRATADO: Suporte ampliado e funcionamento contínuo.
➡️ Explicação: Define compromissos do Lyra RH.

Cláusula 6 – Vigência: Renovação mensal automática.
➡️ Explicação: Estabelece duração.

Cláusula 7 – Cancelamento: Aviso prévio de 15 dias.
➡️ Explicação: Regra de rescisão.

Cláusula 8 – Foro: Foro da sede do CONTRATADO.
➡️ Explicação: Local de disputas.

Este contrato é regido pelas leis brasileiras e ambas as partes concordam com os termos estabelecidos.
    `
  },
  'corporativo-mensal': {
    id: 'corporativo-mensal',
    name: 'Plano Scale',
    monthlyPrice: 849.90,
    annualPrice: 679.90,
    maxHires: 30,
    period: 'monthly',
    features: [
      'Até 30 contratações por mês',
      'R$ 28,33 por contratação',
      'Gestão de alto volume com automação total — produtividade sem limites.'
    ],
    contract: `
📑 Contrato – Plano Scale Mensal (30 contratações por R$ 849,90)

Cláusula 1 – Objeto: Acesso à plataforma para até 30 contratações mensais.
➡️ Explicação: Define volume contratado.

Cláusula 2 – Valor e Pagamento: R$ 849,90 por mês, cobrança mensal via Stripe.
➡️ Explicação: Esclarece custo e cobrança.

Cláusula 3 – Benefícios: Suporte a alto volume e segurança reforçada.
➡️ Explicação: Mostra benefícios para empresas maiores.

Cláusula 4 – Obrigações do CONTRATANTE: Cumprir responsabilidades de gestão e pagamento.
➡️ Explicação: Define deveres do cliente.

Cláusula 5 – Obrigações do CONTRATADO: Suporte dedicado e conformidade garantida.
➡️ Explicação: Compromissos do Lyra RH.

Cláusula 6 – Vigência: Renovação mensal automática.
➡️ Explicação: Estabelece ciclo.

Cláusula 7 – Cancelamento: Aviso prévio de 30 dias.
➡️ Explicação: Regras de rescisão.

Cláusula 8 – Foro: Foro da sede do CONTRATADO.
➡️ Explicação: Local de litígios.

Este contrato é regido pelas leis brasileiras e ambas as partes concordam com os termos estabelecidos.
    `
  },
  'essencial-anual': {
    id: 'essencial-anual',
    name: 'Plano Start',
    monthlyPrice: 219.90,
    annualPrice: 175.90,
    maxHires: 5,
    period: 'annual',
    features: [
      'Até 5 contratações por ano',
      'Desconto de 20% no valor anual',
      'R$ 2.110 total/ano',
      'Comece o ano economizando — 2 meses grátis e suporte garantido.'
    ],
    contract: `
CONTRATO DE PRESTAÇÃO DE SERVIÇOS – PLANO START ANUAL

Cláusula 1 – Objeto: Este contrato tem como objeto a disponibilização da plataforma Lyra RH para gestão de Recursos Humanos, limitada a 05 contratações anuais.

Cláusula 2 – Valor e Pagamento: O valor do plano é de R$ 175,90 por mês, cobrado de forma anual recorrente via Stripe (R$ 2.110 total/ano), já incluso desconto de 20% em relação ao valor mensal.

Cláusula 3 – Obrigações do CONTRATANTE: O cliente deverá utilizar a plataforma corretamente, manter informações atualizadas e cumprir com os pagamentos.

Cláusula 4 – Obrigações do CONTRATADO: O Lyra RH deverá manter a plataforma acessível, exceto em casos de manutenção, e garantir suporte técnico padrão.

Cláusula 5 – Vigência: O contrato terá duração de 12 meses, renovando-se automaticamente se não houver manifestação contrária.

Cláusula 6 – Cancelamento: A rescisão poderá ocorrer mediante aviso prévio de 30 dias.

Cláusula 7 – Foro: Para dirimir dúvidas, fica eleito o foro da sede do CONTRATADO.

Este contrato é regido pelas leis brasileiras e ambas as partes concordam com os termos estabelecidos.
    `
  },
  'profissional-anual': {
    id: 'profissional-anual',
    name: 'Plano Growth',
    monthlyPrice: 379.90,
    annualPrice: 304.90,
    maxHires: 10,
    period: 'annual',
    features: [
      'Até 10 contratações por ano',
      'Desconto de 20% no valor anual',
      'R$ 3.650 total/ano',
      'Cresça com previsibilidade — economize e mantenha seu RH automatizado o ano inteiro.'
    ],
    contract: `
CONTRATO DE PRESTAÇÃO DE SERVIÇOS – PLANO GROWTH ANUAL

Cláusula 1 – Objeto: Disponibilização da plataforma para até 10 contratações anuais.

Cláusula 2 – Valor e Pagamento: R$ 304,90 por mês, cobrança anual via Stripe (R$ 3.650 total/ano).

Cláusula 3 – Benefícios: Escalabilidade, melhor custo-benefício e automação.

Cláusula 4 – Obrigações do CONTRATANTE: Responsabilidade pela veracidade dos dados fornecidos.

Cláusula 5 – Obrigações do CONTRATADO: Garantir suporte e funcionamento pleno da plataforma.

Cláusula 6 – Vigência: Validade de 12 meses.

Cláusula 7 – Cancelamento: Aviso prévio de 30 dias.

Cláusula 8 – Foro: Foro da sede do CONTRATADO.

Este contrato é regido pelas leis brasileiras e ambas as partes concordam com os termos estabelecidos.
    `
  },
  'corporativo-anual': {
    id: 'corporativo-anual',
    name: 'Plano Scale',
    monthlyPrice: 849.90,
    annualPrice: 679.90,
    maxHires: 30,
    period: 'annual',
    features: [
      'Até 30 contratações por ano',
      'Desconto de 20% no valor anual',
      'R$ 8.155 total/ano',
      'Potência máxima para grandes times — economize e escale com segurança.'
    ],
    contract: `
CONTRATO DE PRESTAÇÃO DE SERVIÇOS – PLANO SCALE ANUAL

Cláusula 1 – Objeto: Plataforma disponível para até 30 contratações anuais.

Cláusula 2 – Valor e Pagamento: R$ 679,90 por mês, cobrança anual via Stripe (R$ 8.155 total/ano).

Cláusula 3 – Benefícios: Gestão previsível, segurança reforçada e conformidade.

Cláusula 4 – Obrigações do CONTRATANTE: Cumprir com obrigações administrativas e financeiras.

Cláusula 5 – Obrigações do CONTRATADO: Oferecer suporte dedicado e garantir performance.

Cláusula 6 – Vigência: 12 meses.

Cláusula 7 – Cancelamento: Aviso prévio de 60 dias.

Cláusula 8 – Foro: Foro da sede do CONTRATADO.

Este contrato é regido pelas leis brasileiras e ambas as partes concordam com os termos estabelecidos.
    `
  },
};

export default function PlanDetail() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [acceptedContract, setAcceptedContract] = useState(false);
  const [loading, setLoading] = useState(false);

  const plan = planId ? PLANS_DATA[planId] : null;

  if (!plan) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Plano não encontrado</h1>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  const handlePayment = async () => {
    if (!acceptedContract) {
      toast({
        title: "Aceite necessário",
        description: "Você deve aceitar os termos do contrato para continuar.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { planId: plan.id }
      });

      if (error) throw error;

      if (data?.url) {
        // Open Stripe checkout in a new tab
        window.open(data.url, '_blank');
        
        toast({
          title: "Redirecionando para pagamento",
          description: "Uma nova aba foi aberta com o checkout do Stripe.",
        });
      }
      
    } catch (error: any) {
      toast({
        title: "Erro no pagamento",
        description: error.message || "Ocorreu um erro ao processar o pagamento. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const currentPrice = plan.period === 'annual' ? plan.annualPrice : plan.monthlyPrice;
  const displayPrice = plan.period === 'annual' 
    ? `R$ ${plan.annualPrice.toFixed(2)}/mês (cobrado anualmente)` 
    : `R$ ${plan.monthlyPrice.toFixed(2)}/mês`;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="max-w-6xl mx-auto p-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-3xl font-bold">
            {plan.name} - {plan.period === 'annual' ? 'Anual' : 'Mensal'}
          </h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 grid lg:grid-cols-2 gap-8">
        {/* Plan Summary */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Resumo do Plano</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Valor:</span>
                <span className="font-semibold text-lg">{displayPrice}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Contratações inclusas:</span>
                <span className="font-medium">{plan.maxHires} por {plan.period === 'annual' ? 'ano' : 'mês'}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Periodicidade:</span>
                <span className="font-medium">{plan.period === 'annual' ? 'Anual' : 'Mensal'}</span>
              </div>

              <div className="pt-4 border-t">
                <h4 className="font-medium mb-3">Principais benefícios:</h4>
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start text-sm">
                      <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-primary" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Contract Acceptance */}
          <Card>
            <CardHeader>
              <CardTitle>Aceite do Contrato</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox 
                  id="contract-accept"
                  checked={acceptedContract}
                  onCheckedChange={(checked) => setAcceptedContract(checked as boolean)}
                />
                <label 
                  htmlFor="contract-accept" 
                  className="text-sm leading-relaxed cursor-pointer"
                >
                  Li e aceito os termos do contrato apresentado ao lado
                </label>
              </div>
              
              <Button 
                onClick={handlePayment}
                disabled={!acceptedContract || loading}
                className="w-full"
                size="lg"
              >
                {loading ? "Processando..." : "Finalizar Pagamento via Stripe"}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Contract Text */}
        <div>
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Contrato Completo
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Baixar PDF
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-muted p-4 rounded-lg h-96 overflow-y-auto">
                <div className="text-sm leading-relaxed whitespace-pre-line">
                  {plan.contract}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}